import { useCallback, useMemo } from 'react';
import { FormDataType } from '@tw/tw-component-library';
import { SectionKey } from '../../../../../types';
import {
  FormSchemaConfig,
  EnhancedSection,
  EnhancedGroupedSection,
  FieldDependency,
} from '../../types/form/schema.types';
import { processField } from '../../utils/fieldProcessing';
import { INITIAL_SCHEMAS } from '../../constants/formConstants';
import { SCHEMA_ORDER } from './schemaOrder';
// import { sortSchemas } from '../../utils/sortSchemas';
import { createLogger } from '../../../../../utils/logger';
import { FormState } from '../../types/core/form-state.types';

/**
 * Logger instance for debugging and operational logging
 */
const logger = createLogger('useUnifiedSchemaProcessing');

/**
 * Defines the properties required for unified schema processing:
 *  - formData: Data captured from the form
 *  - schemas: Collection of form configurations keyed by section
 *  - completedSections: List of sections that are completed or in-progress
 *  - formConfig: High-level form configuration object
 *  - refs: Optionally contains mutable references for controlling or tracking specific behaviors
 *  - processDependencies: Callback for processing field-level dependencies
 *  - getDependentFields: Callback to retrieve field names that depend on a given field
 *  - validateDependency: Callback to validate a dependency object
 *  - formState: Complete form state including isComplete flag
 */
interface UnifiedSchemaProcessingProps {
  formData: FormDataType | null;
  schemas: Record<SectionKey, FormSchemaConfig>;
  completedSections: SectionKey[];
  formConfig: any;
  refs?: any;
  processDependencies?: (field: any) => any;
  getDependentFields?: (
    fieldName: string,
    schema: FormSchemaConfig
  ) => string[];
  validateDependency?: (dependency: FieldDependency) => boolean;
  formState?: FormState;
}

/**
 * useUnifiedSchemaProcessing
 *
 * Custom React hook that merges logic from multiple schema-related hooks into one:
 *  1. Processes fields and sections in a schema
 *  2. Handles dependencies among fields and sections
 *  3. Sorts and logs schema data for debugging
 *
 * @param {UnifiedSchemaProcessingProps} props The hook's properties
 * @returns An object containing:
 *  - processedSchemas: The final processed and sorted schemas
 *  - processSchema: A method to individually process a single schema
 *  - processFieldsInSection: A method to process fields within a section
 *  - processAllSchemas: A method to process all provided schemas
 */
export const useUnifiedSchemaProcessing = ({
  formData,
  schemas,
  completedSections,
  formConfig,
  refs,
  processDependencies,
  getDependentFields,
  validateDependency,
  formState,
}: UnifiedSchemaProcessingProps) => {
  /**
   * Debug logging wrapper that only logs when debugMode is enabled in formConfig
   * @param message The debug message to log
   * @param data The supplementary data to include
   */
  const debugLog = useCallback(
    (message: string, data: any) => {
      if (formConfig?.debugMode) {
        logger.state(message, data);
      }
    },
    [formConfig?.debugMode]
  );

  /**
   * Recursive function that processes all fields in a section.
   * - Applies transformations to each field using processField
   * - If sub-sections exist, will process them recursively
   *
   * @param section The section (or grouped section) to process
   * @param schema The schema configuration for the current section
   * @param sectionKey The key identifying this section
   * @returns A new section object with all fields processed
   */
  const processFieldsInSection = useCallback(
    (
      section: EnhancedSection | EnhancedGroupedSection,
      schema: FormSchemaConfig,
      sectionKey: SectionKey
    ): EnhancedSection | EnhancedGroupedSection => {
      // If the section contains a fields array, process each field individually
      if ('fields' in section && section.fields) {
        return {
          ...section,
          fields: section.fields.map((field) =>
            processField(
              field,
              formData || {}, // Safely pass an empty object if formData is null
              schema,
              sectionKey,
              completedSections,
              formState
            )
          ),
        };
      }

      // If the section contains nested sections, recurse for each sub-section
      if ('sections' in section && section.sections) {
        return {
          ...section,
          sections: section.sections.map((subSection) =>
            processFieldsInSection(subSection, schema, sectionKey)
          ),
        };
      }

      // Base case: return the section unmodified if no further processing can be done
      return section;
    },
    [formData, completedSections, formState]
  );

  /**
   * Processes a single schema by:
   *  1. Processing fields in each section
   *  2. Optionally handling field dependencies if enabled
   *
   * @param schema Schema configuration object
   * @param data The form data used for conditional rendering / validations
   * @param sectionKey The key identifying the schema
   * @returns A transformed schema with processed sections, fields, and dependencies
   */
  const processSchema = useCallback(
    (
      schema: FormSchemaConfig,
      data: FormDataType,
      sectionKey: SectionKey
    ): FormSchemaConfig => {
      // mark param as used for linter
      void data;
      // If there's no valid sections array, return empty
      if (!schema?.sections) return { sections: [] };

      // Process each section and optionally its fields
      const processedSections = schema.sections.map((section) => {
        // Initially transform fields in this section
        let processedSection = processFieldsInSection(
          section,
          schema,
          sectionKey
        );

        // If dependency processing is enabled, update fields accordingly
        // Only process dependencies for the active section (currentSection)
        // Use formState.currentSection directly but don't depend on entire formState object
        const isActiveSection = formState?.currentSection
          ? formState.currentSection === sectionKey
          : true;
        if (
          formConfig?.enableDependencyProcessing &&
          processDependencies &&
          isActiveSection
        ) {
          if ('fields' in processedSection && processedSection.fields) {
            // Process dependencies for the entire section's fields in one go
            const processedFieldsArray = processDependencies(
              processedSection.fields
            ) as EnhancedSection['fields'];

            // Optionally validate dependent fields relationships
            if (getDependentFields && validateDependency) {
              processedFieldsArray?.forEach((field) => {
                const dependentFields = getDependentFields(field.name, schema);
                dependentFields.forEach((depFieldName) => {
                  const depField = processedFieldsArray?.find(
                    (f) => f.name === depFieldName
                  );
                  if (depField?.dependencies) {
                    depField.dependencies.forEach((dep) => {
                      validateDependency(dep);
                    });
                  }
                });
              });
            }

            processedSection = {
              ...processedSection,
              fields: processedFieldsArray,
            };
          }
        }

        return processedSection;
      });

      return {
        ...schema,
        sections: processedSections,
      };
    },
    [
      formConfig?.enableDependencyProcessing,
      processDependencies,
      getDependentFields,
      validateDependency,
      processFieldsInSection,
      // Only depend on currentSection, not entire formState object
      // This prevents unnecessary recreations when formState object reference changes
      formState?.currentSection,
    ]
  );

  /**
   * Orchestrates the processing of all schemas in the provided object:
   *  1. Logs the start of processing if debugMode is on
   *  2. Processes each schema in order, respecting predefined SCHEMA_ORDER
   *  3. Sorts the final schemas using sortSchemas
   *  4. Logs the completion of processing if debugMode is on
   *
   * @returns The fully processed and sorted schemas
   */
  const processAllSchemas = useCallback(() => {
    debugLog('Starting unified schema processing', {
      hasFormData: !!formData,
      schemaKeys: Object.keys(schemas),
      isReassignment: refs?.reassignmentInProgress?.current,
    });

    // If form data doesn't exist, warn and return a fallback set of schemas
    if (!formData) {
      logger.warning('No form data available, returning initial schemas');
      return INITIAL_SCHEMAS;
    }

    // Iterate over each schema, transform it, and accumulate results
    const processedSchemas = Object.entries(schemas).reduce<
      Record<SectionKey, FormSchemaConfig>
    >(
      (acc, [key, schema]) => {
        const sectionKey = key as SectionKey;
        const orderConfig = SCHEMA_ORDER[sectionKey];

        // Transform the schema with fields and dependencies
        const processedSchema = processSchema(schema, formData, sectionKey);

        // Special handling for a "request_reassign" section if present
        //TODO: Need to resolve this
        if (sectionKey === 'request_reassign') {
          // Build dependencies that affect other sections
          const affectedSectionDependencies =
            (SCHEMA_ORDER.request_reassign.affectedSections?.map((section) => ({
              // Intentionally using a relaxed shape; cast after creation to satisfy types
              field: section,
              type: 'section',
              condition: {
                operator: 'equals',
                value: true,
              },
            })) as unknown as FieldDependency[]) || [];

          // Validate those dependencies
          if (validateDependency) {
            affectedSectionDependencies.forEach((dep) => {
              validateDependency(dep);
            });
          }

          // Merge the newly created dependencies into the schema
          acc[sectionKey] = {
            ...processedSchema,
            order: orderConfig,
            validation: {
              ...processedSchema.validation,
              dependencies: {
                ...processedSchema.validation?.dependencies,
                fields: affectedSectionDependencies,
              },
            },
          };

          debugLog('Processed reassignment schema', {
            sectionKey,
            affectedSections: SCHEMA_ORDER.request_reassign.affectedSections,
            dependenciesCount: affectedSectionDependencies.length,
          });

          return acc;
        }

        // For any other section, just apply the new order config
        acc[sectionKey] = {
          ...processedSchema,
          order: orderConfig,
        };

        // Log the results for debugging if enabled
        debugLog('Processed regular schema', {
          sectionKey,
          fieldsCount: processedSchema.sections?.reduce(
            (count, section) =>
              count +
              ('fields' in section
                ? section.fields?.length || 0
                : section.sections?.reduce(
                    (subCount, subSection) =>
                      subCount + (subSection.fields?.length ?? 0),
                    0
                  ) || 0),
            0
          ),
        });

        return acc;
      },
      { ...INITIAL_SCHEMAS }
    );

    // Once all schemas are processed, sort them and log the completion
    // const sortedSchemas = sortSchemas(processedSchemas);

    debugLog('Completed unified schema processing', {
      schemaCount: Object.keys(processedSchemas).length,
      hasReassignment: 'request_reassign' in processedSchemas,
    });

    return processedSchemas;
  }, [
    formData,
    schemas,
    refs?.reassignmentInProgress,
    processSchema,
    validateDependency,
    debugLog,
  ]);

  /**
   * Memoized result of processing all schemas, to avoid unnecessary re-computation.
   */
  const processedSchemas = useMemo(() => {
    return processAllSchemas();
  }, [processAllSchemas]);

  // Return all relevant methods or data for external usage
  return {
    processedSchemas,
    processSchema,
    processFieldsInSection,
    processAllSchemas,
  };
};
